// @flow
import * as React from 'react';

import { FORM_ERROR } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { addCartProduct, getCart, selectCartUUID } from '@gandi/cart';
import type { ProductData } from '@gandi/cart';
import { Form } from '@gandi/form/FinalForm';
import { useTranslator } from '@gandi/translate';

import { withRouter } from 'src/helpers/withRouter';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { validateCertificateOptions } from '../modules/certificate';
import type { FormValues, FormErrors } from '../types';

/**
 * Utility pretty similar to `notifyMaybeApiError` (`useNotification`)
 */
const getMaybeApiError = (error: any, defaultMessage: string) => {
  return (
    (error.response?.data?.errors || [])[0]?.description ||
    error.response?.data?.message ||
    error.message ||
    defaultMessage
  );
};

const computeInitialFormValuesDefault = (): FormValues => ({
  provider: 'digicert',
  type: 'standard',
  protect: 'single',
  /**
   * by default we set apex only for digicert/std/single to prevent mistake for the customer
   */
  apex_and_www: false,
  dcvMethod: 'dns', // FIXME: useless for auto cert?
  autoDcvValid: false,
});
const initialFormValuesDefault = computeInitialFormValuesDefault();

type OwnProps = {
  children: React.Node,
  formDataMapper: (formValues: FormValues) => void | ProductData,
  initialValues?: void | Partial<FormValues>,
};

type Props = {
  ...OwnProps,
};
function CertificateFormConcrete({
  formDataMapper,
  children,
  initialValues,
}: Props): React.MixedElement {
  const dispatch = useDispatch();
  const { __ } = useTranslator();
  const navigate = useNavigate();
  const cartId = useAppSelector(selectCartUUID);

  /**
   * Add a certificate product to the cart and redirect to the overview.
   */
  const onSubmit = async (
    formData: FormValues,
  ): Promise<void | FormErrors<$Keys<FormValues>>> => {
    const productData = formDataMapper(formData);

    if (!productData) {
      return undefined;
    }

    try {
      const errors = await dispatch(validateCertificateOptions(productData));

      if (errors.length) {
        // this adds a generic error to the whole form
        // $FlowFixMe[incompatible-return] TODO Fix type
        return {
          [FORM_ERROR]: errors,
        };
      }
    } catch (er) {
      // This should not happen, we let the user to add his cert to cart (this has been logged).
    }

    try {
      await dispatch(
        addCartProduct({
          productData,
        }),
      );
    } catch (er) {
      const description = getMaybeApiError(
        er,
        __('An error has occurred. Please try again later.'),
      );

      // $FlowFixMe[incompatible-return] TODO Fix type
      return {
        [FORM_ERROR]: [{ description }],
      };
    }

    await dispatch(getCart());

    // if success, redirect to the overview
    // redirect to the overview
    navigate(`/cart/${cartId}`);

    return undefined;
  };

  return (
    <Form
      onSubmit={onSubmit}
      mutators={arrayMutators}
      initialValues={{ ...initialFormValuesDefault, ...initialValues }}
    >
      {children}
    </Form>
  );
}

export const CertificateForm: React.ComponentType<OwnProps> = withRouter(
  CertificateFormConcrete,
);
